import { Button } from '@/components/ui/button'
import { PrinterIcon, ReceiptTextIcon } from 'lucide-react'
import { Route as receiptRouter } from '@/routes/(pharma)/_layout/receipt/$saleId'
import parsePhoneNumber from 'libphonenumber-js'
import dayjs from 'dayjs'
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { convertToCurrencyRounded } from '@/utils/currency_converter'
import { useNavigate } from '@tanstack/react-router'
import { usePrint } from '@/layouts/layout/component'
import { useCallback, useEffect, useRef } from 'react'
import { PaymentMode, SaleStatus } from '@/gen/sales/common_pb'

const Receipt = () => {
    const saleData = receiptRouter.useLoaderData()
    const searchParams = receiptRouter.useSearch()
    const pathParams = receiptRouter.useParams()
    const navigate = useNavigate()
    const print = usePrint()
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (print.printMode) {
            window.print()
            print.setPrintMode(false)
        }
    }, [print.printMode])


    const pharmacySettings = localStorage.getItem("pharmacySettings") ? JSON.parse(localStorage.getItem("pharmacySettings")!) : null
    const pharmacyMetadata = localStorage.getItem("pharmacyMetadata") ? JSON.parse(localStorage.getItem("pharmacyMetadata")!) : null
    const pharmacyLocation = localStorage.getItem("pharmacyLocationDetails") ? JSON.parse(localStorage.getItem("pharmacyLocationDetails")!) : null

    return (
        <div className={`${print.printMode ? "" : 'overflow-y-scroll h-screen pb-48'}`} ref={containerRef}>
            <div className="flex flex-col w-full items-center justify-center text-center overflow-auto py-4 mx-auto max-w-[700px] px-1">
                <div className="w-full">
                    <h1 className="text-2xl font-semibold">
                        {localStorage.getItem("pharmacyName")}
                    </h1>
                    {/* <p className='text-sm text-gray-500'>Wed, April 27, 2023 • 9:27:53 AM</p> */}
                    <p className="text-sm text-gray-500">
                        {dayjs(saleData.saleValues?.createdAt?.toDate()).format(`${pharmacySettings.invoiceDateFormat} | ${pharmacySettings.invoiceTimeFormat}`)}
                    </p>
                    <p className="text-sm text-gray-500">
                        {
                            pharmacyMetadata.taxIdentifiers.map((identifier: any) => {
                                return (
                                    <span key={identifier.type}>
                                        {identifier.type}: {identifier.value}
                                        {pharmacyMetadata.taxIdentifiers.length > 1 ? <span className='px-1'>&bull;</span> : ""}
                                    </span>
                                )
                            })
                        }
                    </p>
                    <p className="text-sm text-gray-500">
                        {/* Phone Number: {pharmacyInfo.pharmacyPhone} */}
                        Phone Number: {parsePhoneNumber(pharmacyMetadata.primaryPhone, pharmacyMetadata.countryCode)!.formatInternational()}
                    </p>
                    <p className="text-sm text-gray-500">
                        {/* Address: {pharmacyInfo.pharmacyAddress} */}
                        Address: {pharmacyLocation.address}
                    </p>
                    <div className="border-dashed border-2 border-gray-500 rounded-lg p-4 text-center relative mt-8">
                        <span className="absolute -top-4 left-0 right-0 mx-auto text-gray-500 text-xs font-bold uppercase tracking-wide w-full grid place-items-center">
                            <p className="bg-white z-10 px-4 text-lg">Invoice No</p>
                        </span>
                        <div className="">
                            <p className="font-semibold">
                                {saleData.receiptNumber}
                            </p>
                        </div>
                    </div>

                    {saleData.isValidInsurance ? <div className="flex items-center justify-between text-sm mt-4">
                        <p className="text-gray-500">Insurance Used</p>
                        <p>Yes</p>
                    </div> : null}
                    {saleData.isValidInsurance ? <div className="flex items-center justify-between text-sm mt-4">
                        <p className="text-gray-500">Insurance Name</p>
                        <p>{saleData.insurance?.insuranceProviderId}</p>
                    </div> : null}

                    {saleData.saleValues?.status === SaleStatus.FULLY_REFUNDED || saleData.saleValues?.status === SaleStatus.VOIDED ? <div className="flex items-center justify-between text-sm mt-4">
                        <p className="text-gray-500">Payment Status</p>
                        <p>{
                            saleData.saleValues?.status === SaleStatus.FULLY_REFUNDED ? "Fully Refunded" :
                                saleData.saleValues?.status === SaleStatus.VOIDED ? "Voided" :
                                saleData.saleValues?.status === SaleStatus.PARTIALLY_REFUNDED ? "Partially Refunded" :
                                saleData.saleValues.status === SaleStatus.COMPLETED ? "Completed" :
                                "Pending"
                            }</p>
                    </div> : null}

                    <hr className="my-4" />

                    {saleData.isValidPatient ? <div className="flex items-center justify-between text-sm mt-4">
                        <p className="text-gray-500">Customer Name</p>
                        {saleData.patient?.name}
                    </div> : null}

                    <div className="flex items-center justify-between text-sm mt-2">
                        <p className="text-gray-500">Customer Type</p>
                        <p>{saleData.isValidPatient ? "Registered" : "OTC"}</p>
                    </div>

                    {saleData.isValidPatient ? <div className="flex items-center justify-between text-sm mt-4">
                        <p className="text-gray-500">Customer Phone</p>
                        {parsePhoneNumber(saleData.patient?.phone!, pharmacyMetadata.countryCode)!.formatInternational()}
                    </div> : null}

                    <hr className="my-4" />
                    {saleData.items.map((item, index) => (
                        <div key={index} className="flex items-center justify-between text-sm mt-2">
                            <p className="text-gray-500 text-left">
                                {item.tradeName}{" "} {item.unit} {" "}{item.unitType}
                                <br />
                                <span className="text-xs">
                                    {item.batchNumber}//
                                    {dayjs(item.expiryDate?.toDate()).format("DD-MM-YYYY")}
                                </span>
                                <br />
                                <span>
                                    {item.taxes.map((tax, taxIndex) => (
                                        <span key={taxIndex} className="text-xs text-gray-500">
                                            {tax.componentCode}: {convertToCurrencyRounded(tax.amount)}
                                            {taxIndex < item.taxes.length - 1 ? ', ' : ''}  
                                        </span>
                                    ))}
                                </span>
                            </p>
                            <div className='text-right'>
                                <p className='text-gray-500 text-xs'>{convertToCurrencyRounded(item.unitPrice)} x {item.quantity}</p>
                                <p>{convertToCurrencyRounded(item.unitPrice * item.quantity)}</p>
                            </div>
                        </div>
                    ))}
                    <hr className="my-4" />

                    <div className="flex items-center justify-between text-sm mt-4">
                        <p className="text-gray-500">Total Items</p>
                        {/* <p>{salesItems.length}</p> */}
                        <p>{saleData.items.length}</p>
                    </div>

                    <hr className="my-4" />

                    <div className="flex items-center justify-between text-sm mt-4">
                        <p className="text-gray-500">Amount</p>
                        <p>{convertToCurrencyRounded(saleData.saleValues?.subTotalAmount!)}</p>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-4">
                        <p className="text-gray-500">Discount</p>
                        <p>{convertToCurrencyRounded(saleData.saleValues?.totalItemDiscountAmount!)}</p>
                    </div>

                    <div className="flex items-center justify-between text-sm mt-2">
                        <p className="text-gray-500">Tax</p>
                        <p>{convertToCurrencyRounded(saleData.saleValues?.totalTaxAmount!)}</p>
                    </div>

                    <div className="flex items-center justify-between text-sm mt-2">
                        <p className="text-gray-500">Total Paid</p>
                        <p>{convertToCurrencyRounded(saleData.saleValues?.grandTotalAmount!)}</p>
                    </div>

                    <hr className="my-4" />
                    <div className="flex items-center justify-between text-sm mt-4">
                        <p className="text-gray-500">Payment Information</p>
                    </div>
                    {
                        saleData.payments.map((payment, index) => (
                            <div key={index} className="flex items-center justify-between text-sm">
                                <p className="text-gray-500">{
                                        Object.values(PaymentMode).includes(payment.paymentMode) ?
                                            PaymentMode[payment.paymentMode]
                                            : payment.paymentMode
                                    }</p>
                                <p>{convertToCurrencyRounded(payment.amount)}</p>
                            </div>
                        ))
                    }
                    <div className="flex items-center justify-between text-sm mt-4">
                        <p className="text-gray-500">Pharmacist</p>
                        <p>{saleData.createdBy?.fullName}</p>
                    </div>
                    <hr className="my-4" />
                    <div className='text-sm text-gray-500'>
                        <p>Tax Breakdown</p>
                        <div className='flex flex-col space-y-2 mt-2'>
                            {
                                // Combine all taxes from all items and sum the amounts by componentCode
                                (() => {
                                    const taxMap: Record<string, number> = {};
                                    saleData.items.forEach(item => {
                                        item.taxes.forEach(tax => {
                                            if (taxMap[tax.componentCode + " (" + tax.rate.toFixed(2) + "%)"]) {
                                                taxMap[tax.componentCode + " (" + tax.rate.toFixed(2) + "%)"] += tax.amount;
                                            } else {
                                                taxMap[tax.componentCode + " (" + tax.rate.toFixed(2) + "%)"] = tax.amount;
                                            }
                                        });
                                    });
                                    return Object.entries(taxMap).map(([componentCode, amount]) => (
                                        <div key={componentCode} className='flex items-center justify-between'>
                                            <p className='text-xs'>{componentCode}</p>
                                            <p className='text-xs'>{convertToCurrencyRounded(amount)}</p>
                                        </div>
                                    ));
                                })()
                            }
                        </div>
                    </div>

                    <div className='flex flex-col space-y-4 mt-8 print:hidden'>
                        <Button
                            className='bg-indigo-600 rounded-full'
                            onClick={() => {
                                navigate({
                                    to: searchParams.return_to === "sales_history" ? "/overview/sales_history" : searchParams.return_to === "sales_history_one" ? "/overview/sales_history/" + pathParams.saleId : "/sales",
                                })
                            }}
                        >
                            {searchParams.return_to === "sales_history" ? "Back to Sales History" : searchParams.return_to === "sales_history_one" ? "Back to Sale Info" : "Back to Sales"}
                        </Button>

                        <Button
                            variant={'outline'}
                            className='rounded-full'
                        >
                            <ReceiptTextIcon className='w-4 h-4 mr-2' />
                            Send E-receipt
                        </Button>

                        <Button
                            variant={'outline'}
                            className='rounded-full'
                            onClick={() => {
                                print.setPrintMode(true)
                            }}
                        >
                            <PrinterIcon className='w-4 h-4 mr-2' />
                            Print PDF Receipt
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Receipt